import { useState, useEffect, useRef, useCallback } from 'react';
import { uploadAttachment, devApi } from '../api.js';
import { MAX_ATTACHMENT_BYTES, fileExt, formatBytes, isImage } from '../format.js';
import { CopyImageButton, Lightbox } from './AttachmentGallery.jsx';

// Ctrl+V is the point of this control, so the listener is on the document rather than on a
// focused drop zone — a screenshot is pasted the moment it is taken, not after hunting for the
// right box to click first. It only acts when the clipboard actually carries an image, so a
// normal text paste into the title or description field is untouched.
//
// Paste stays images-only even though the control now takes any file. A clipboard carrying a
// file is almost always a screenshot; a clipboard carrying rich text from Word or Excel also
// carries file items, and silently attaching those to a task because somebody pasted a sentence
// into the description is a surprise. Files that are not images arrive by drop or by picker,
// where the intent is unambiguous.
function imagesFrom(clipboardData) {
  return [...(clipboardData?.items || [])]
    .filter(it => it.kind === 'file' && it.type.startsWith('image/'))
    .map(it => it.getAsFile())
    .filter(Boolean);
}

function tooLarge(files) {
  return files.filter(f => f.size > MAX_ATTACHMENT_BYTES);
}

/** An image thumbnail. `onOpen` and `copyUrl` are set only for an attachment that is saved:
 *  a paste still travelling has no URL to copy from and nothing to open. */
function Thumb({ src, name, sub, onRemove, progress, onOpen, copyUrl }) {
  return (
    <figure style={{
      margin: 0, width: 164, border: '1px solid var(--rule)', borderRadius: 'var(--r-md)',
      background: 'var(--surface)', overflow: 'hidden',
    }}>
      {/* The image itself opens the viewer, as it does everywhere else an image is shown.
          The button below repeats it because a picture that happens to be clickable is not an
          affordance anybody finds. */}
      <button type="button" disabled={!onOpen} onClick={onOpen}
        title={onOpen ? `${name} — click to preview` : name}
        style={{ display: 'block', width: '100%', height: 92, border: 0, padding: 0,
                 background: 'var(--surface-2)', cursor: onOpen ? 'zoom-in' : 'default' }}>
        {src
          ? <img src={src} alt={name}
              style={{ maxWidth: '100%', maxHeight: 92, display: 'block', objectFit: 'contain',
                       margin: '0 auto' }} />
          : <span className="o-note" style={{ margin: 0 }}>No preview</span>}
      </button>
      {progress !== undefined && progress < 1 && (
        <div className="o-progress"><i style={{ width: `${Math.round(progress * 100)}%` }} /></div>
      )}
      <figcaption style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4,
        padding: '6px 4px 6px 8px', borderTop: '1px solid var(--rule-soft)',
      }}>
        <span style={{
          fontSize: '.75rem', color: 'var(--text-2)', overflow: 'hidden',
          textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }} title={name}>{sub || name}</span>
        {onRemove && (
          <button type="button" className="o-x" onClick={onRemove}
            aria-label={`Remove ${name}`}>&times;</button>
        )}
      </figcaption>
      {/* Their own row rather than beside the name: the column this picker now lives in is
          half the width it had, and three controls on one line is three controls clipped. */}
      {(onOpen || copyUrl) && (
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between',
                      gap: 2, padding: '0 2px 3px',
                      borderTop: '1px solid var(--rule-soft)' }}>
          <button type="button" className="o-btn o-btn-ghost o-btn-sm" disabled={!onOpen}
            onClick={onOpen}>Preview</button>
          <CopyImageButton url={copyUrl} size="o-btn-sm" />
        </div>
      )}
    </figure>
  );
}

/** Everything that is not an image: no preview to show, so the name and size are the card. */
function FileCard({ name, size, sub, onRemove, progress, onOpen }) {
  return (
    <div className="o-file" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span className="o-ext">{fileExt(name)}</span>
        {onOpen
          ? <button type="button" className="o-file-name" onClick={onOpen}
              title={`Download ${name}`}
              style={{ border: 0, background: 'none', padding: 0, font: 'inherit',
                       textAlign: 'left', cursor: 'pointer' }}>
              {name}
              <span className="o-file-sub">{sub || formatBytes(size) || 'Download'}</span>
            </button>
          : <span className="o-file-name" title={name}>
              {name}
              <span className="o-file-sub">{sub || formatBytes(size) || 'File'}</span>
            </span>}
        {onRemove && (
          <button type="button" className="o-x" onClick={onRemove}
            aria-label={`Remove ${name}`}>&times;</button>
        )}
      </div>
      {progress !== undefined && progress < 1 && (
        <div className="o-progress"><i style={{ width: `${Math.round(progress * 100)}%` }} /></div>
      )}
    </div>
  );
}

function Attachment({ file, ...rest }) {
  return file.isImage ? <Thumb {...rest} /> : <FileCard {...rest} />;
}

/**
 * Paste, drop or pick the files attached to a task.
 *
 * Two modes, because a file needs a task to belong to. With `taskId` it uploads immediately and
 * lands in `saved`. Without one — the create form, where the task does not exist yet — it is
 * held in `pending` as an object URL and the caller uploads the batch once the task has an id.
 *
 * The API underneath still calls these screenshots; that is the route and table name from when
 * images were all it took, and renaming a live endpoint buys nothing a comment does not.
 */
export default function AttachmentPicker({
  taskId = null, saved = [], onSavedChange, pending = [], onPendingChange, disabled = false,
}) {
  const [uploading, setUploading] = useState([]);   // [{ key, name, url, size, progress }]
  const [err, setErr] = useState(null);
  const [dragging, setDragging] = useState(false);
  // Which saved image the viewer is showing. Only saved ones: a paste that is still uploading
  // has an object URL and no server-side blob, so opening it would show the local copy and
  // stop working the moment the URL is revoked.
  const [open, setOpen] = useState(null);           // null | index into `savedImages`
  const fileInput = useRef(null);
  // Read inside the document listener so it always sees the current props rather than the ones
  // captured when the listener was attached.
  const latest = useRef({});
  latest.current = { taskId, saved, onSavedChange, pending, onPendingChange, disabled };

  const addFiles = useCallback(async (incoming) => {
    const s = latest.current;
    if (s.disabled || !incoming.length) return;

    const oversized = tooLarge(incoming);
    const files = incoming.filter(f => f.size <= MAX_ATTACHMENT_BYTES);
    setErr(oversized.length
      ? `${oversized.map(f => f.name || 'That file').join(', ')} — over the `
        + `${formatBytes(MAX_ATTACHMENT_BYTES)} limit, so ${oversized.length === 1 ? 'it was' : 'they were'} not attached.`
      : null);
    if (!files.length) return;

    if (!s.taskId) {
      s.onPendingChange?.([
        ...s.pending,
        ...files.map(file => ({
          key: `${Date.now()}-${Math.random()}`,
          file,
          isImage: file.type.startsWith('image/'),
          // Only an image has anything to preview, so only an image gets an object URL to revoke.
          url: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
        })),
      ]);
      return;
    }

    for (const file of files) {
      const key = `${Date.now()}-${Math.random()}`;
      const isImage = file.type.startsWith('image/');
      const url = isImage ? URL.createObjectURL(file) : null;
      setUploading(u => [...u,
        { key, name: file.name || 'attachment', url, isImage, size: file.size, progress: 0 }]);
      try {
        const shot = await uploadAttachment(latest.current.taskId, file,
          p => setUploading(u => u.map(x => (x.key === key ? { ...x, progress: p } : x))));
        latest.current.onSavedChange?.([...latest.current.saved, shot]);
      } catch (e) {
        setErr(e.message);
      } finally {
        if (url) URL.revokeObjectURL(url);
        setUploading(u => u.filter(x => x.key !== key));
      }
    }
  }, []);

  useEffect(() => {
    const onPaste = (e) => {
      const imgs = imagesFrom(e.clipboardData);
      if (!imgs.length) return;
      e.preventDefault();
      addFiles(imgs);
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [addFiles]);

  // Object URLs for pending files are owned by the parent's state, so only the ones created
  // here get revoked here.
  useEffect(() => () => uploading.forEach(u => u.url && URL.revokeObjectURL(u.url)), []); // eslint-disable-line react-hooks/exhaustive-deps

  const removeSaved = async (shot) => {
    setErr(null);
    try {
      await devApi.deleteAttachment(taskId, shot.id);
      onSavedChange?.(saved.filter(s => s.id !== shot.id));
    } catch (e) { setErr(e.message); }
  };

  const removePending = (item) => {
    if (item.url) URL.revokeObjectURL(item.url);
    onPendingChange?.(pending.filter(p => p.key !== item.key));
  };

  const count = saved.length + pending.length + uploading.length;

  // The viewer steps through the saved IMAGES, in the order they are shown — not through every
  // attachment, or the arrows would land on a log file with nothing to display.
  const savedImages = saved.filter(isImage);
  const step = (delta) => setOpen(
    i => (i === null ? i : (i + delta + savedImages.length) % savedImages.length));

  return (
    <div>
      <div
        className="o-drop"
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={e => {
          e.preventDefault(); setDragging(false);
          addFiles([...(e.dataTransfer?.files || [])]);
        }}
        style={dragging ? { borderColor: 'var(--brass)', background: 'var(--surface)' } : undefined}
      >
        <p className="o-hint" style={{ margin: 0 }}>
          <b>Paste a screenshot with <kbd>Ctrl</kbd>+<kbd>V</kbd>.</b> Drop any file here, or{' '}
          <button type="button" className="o-btn o-btn-ghost o-btn-sm" disabled={disabled}
            onClick={() => fileInput.current?.click()}>choose a file</button>
        </p>
        <p className="o-hint" style={{ margin: '4px 0 0' }}>
          Images, logs, exports, documents — up to {formatBytes(MAX_ATTACHMENT_BYTES)} each.
        </p>
        <input ref={fileInput} type="file" multiple hidden
          onChange={e => { addFiles([...e.target.files]); e.target.value = ''; }} />
      </div>

      {err && <p className="o-state o-error" style={{ padding: '10px 0', textAlign: 'left' }}>{err}</p>}

      {count > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 12 }}>
          {saved.map(s => (
            <Attachment key={s.id} file={{ isImage: isImage(s) }}
              src={s.view_url} name={s.file_name || 'attachment'} size={s.byte_size}
              copyUrl={s.view_url}
              onOpen={s.view_url
                ? () => (isImage(s)
                    ? setOpen(savedImages.findIndex(x => x.id === s.id))
                    // A log or an export has nothing to look at. The read URL carries a
                    // download disposition, so this saves the file under its own name.
                    : window.open(s.view_url, '_blank', 'noopener'))
                : null}
              onRemove={disabled ? null : () => removeSaved(s)} />
          ))}
          {pending.map(p => (
            <Attachment key={p.key} file={p} src={p.url} name={p.file.name || 'attachment'}
              size={p.file.size} sub="Not saved yet" onRemove={() => removePending(p)} />
          ))}
          {uploading.map(u => (
            <Attachment key={u.key} file={u} src={u.url} name={u.name} size={u.size}
              sub="Uploading…" progress={u.progress} />
          ))}
        </div>
      )}

      {open !== null && savedImages[open] && (
        <Lightbox
          shot={savedImages[open]}
          index={open}
          count={savedImages.length}
          onClose={() => setOpen(null)}
          onPrev={() => step(-1)}
          onNext={() => step(1)}
        />
      )}
    </div>
  );
}
